import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X, Send, Users, User } from 'lucide-react';
import client from '@/api/client';

export type SendTarget =
  | { type: 'broadcast' }                   // Admin → all owners; Owner → all their supervisors
  | { type: 'user'; userId: string; name: string };  // Admin → specific user

interface Props {
  target: SendTarget;
  onClose: () => void;
  /** Label shown in the "To:" row — e.g. "All Hostel Owners" or "All Supervisors" */
  recipientLabel?: string;
}

export function SendNotificationModal({ target, onClose, recipientLabel }: Props) {
  const { t } = useTranslation();
  const [title,   setTitle]   = useState('');
  const [message, setMessage] = useState('');
  const [sent,    setSent]    = useState(false);
  const [count,   setCount]   = useState<number | null>(null);
  const qc = useQueryClient();

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (target.type === 'broadcast') {
        const res = await client.post<{ sent: number }>('/api/notifications/broadcast', { title, message });
        // interceptor unwraps envelope — res is already { sent: N }
        return (res as unknown as { sent: number }).sent ?? null;
      } else {
        await client.post('/api/notifications/send', { userId: target.userId, title, message });
        return 1;
      }
    },
    onSuccess: (n) => {
      setCount(n);
      setSent(true);
      // Refresh the sender's notification bell immediately
      void qc.invalidateQueries({ queryKey: ['notifications-unread'] });
      void qc.invalidateQueries({ queryKey: ['notifications-list'] });
    },
  });

  const toLabel = target.type === 'user'
    ? target.name
    : (recipientLabel ?? (target.type === 'broadcast' ? t('notifications.allRecipients', 'All recipients') : t('notifications.recipients', 'Recipients')));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-chrome-page rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Send size={17} className="text-teal-600" />
            <h2 className="text-base font-semibold text-gray-900">{t('notifications.sendTitle', 'Send Notification')}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={18} />
          </button>
        </div>

        {sent ? (
          // Success state
          <div className="px-5 py-10 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Send size={22} className="text-green-600" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1">
              {count != null && count > 1 ? t('notifications.sentToCount', 'Sent to {{count}} people', { count }) : t('notifications.sent', 'Notification sent')}
            </p>
            <p className="text-sm text-gray-500">
              {t('notifications.deliveredMessage', 'Your message has been delivered as an in-app notification')}
              {count != null && count > 1 ? '' : t('notifications.deliveredTo', ' to {{name}}', { name: toLabel })}.
            </p>
            <button
              onClick={onClose}
              className="mt-5 bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition"
            >
              {t('common.done', 'Done')}
            </button>
          </div>
        ) : (
          // Compose form
          <div className="px-5 py-4 space-y-4">
            {/* To */}
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2.5">
              {target.type === 'broadcast'
                ? <Users size={15} className="text-gray-400 shrink-0" />
                : <User  size={15} className="text-gray-400 shrink-0" />}
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-wide font-medium">{t('notifications.toLabel', 'To')}</p>
                <p className="text-sm font-medium text-gray-800">{toLabel}</p>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('notifications.titleLabel', 'Title')} *</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t('notifications.titlePlaceholder', 'e.g. Maintenance Notice')}
                maxLength={80}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('notifications.messageLabel', 'Message')} *</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={t('notifications.messagePlaceholder', 'Write your message here…')}
                rows={4}
                maxLength={500}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
              <p className="text-right text-[11px] text-gray-400 mt-0.5">{message.length}/500</p>
            </div>

            {sendMutation.isError && (
              <p className="text-sm text-red-600">{(sendMutation.error as Error).message}</p>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button
                onClick={onClose}
                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => sendMutation.mutate()}
                disabled={!title.trim() || !message.trim() || sendMutation.isPending}
                className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
              >
                <Send size={14} />
                {sendMutation.isPending ? t('notifications.sending', 'Sending…') : t('notifications.send', 'Send')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
